import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@shared/ui/command';
import { Button } from '@shared/ui/button';
import { SuiteIcon } from '@shared/icons';
import { useSearchIndex } from './useSearchIndex';

export const GlobalSearch: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { recentPages, permittedSuites, pages } = useSearchIndex();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelect = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        className="h-8 px-2.5 text-xs text-navbar-muted hover:text-navbar-foreground hover:bg-navbar-accent gap-2 border border-navbar-border/60 rounded-md"
        aria-label="Search across platform"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden md:inline">Search...</span>
        <kbd className="pointer-events-none hidden md:inline-flex h-4 select-none items-center gap-1 rounded bg-white/10 px-1.5 font-mono text-[10px] font-medium text-navbar-muted">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a suite, page, or search query..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {recentPages.length > 0 && (
            <CommandGroup heading="Recent Pages">
              {recentPages.slice(0, 5).map((page) => (
                <CommandItem key={page.key} onSelect={() => handleSelect(page.path)}>
                  <SuiteIcon name={page.icon} className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{page.title}</span>
                  <span className="ml-auto text-xs text-muted-foreground font-mono">
                    {page.path}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          <CommandSeparator />

          <CommandGroup heading="Suites">
            {permittedSuites.map((suite) => (
              <CommandItem key={suite.id} onSelect={() => handleSelect(suite.basePath)}>
                <SuiteIcon name={suite.icon} className="mr-2 h-4 w-4 text-primary" />
                <span>{suite.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Pages">
            {pages.map((p) => (
              <CommandItem key={p.id} onSelect={() => handleSelect(p.path)}>
                <SuiteIcon name={p.icon} className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{p.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">{p.suiteName}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};
